// ScanDetailPage — recent scans for a single domain, drilled from DomainsPage.
// URL: /domains/:domainId/scans
// Fetches GET /api/v1/scans?domain_id=<domainId>.

import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, XCircle, Clock, ScanLine } from "lucide-react";
import { fetchScans, type Scan } from "../api";
import { ErrorState } from "@avagency/frontend/components/shell/ErrorState";
import { LoadingState } from "@avagency/frontend/components/shell/LoadingState";
import { Footer } from "../components/Footer";

// Grade pill styles — full strings for Tailwind JIT
const GRADE_BG: Record<string, { bg: string; border: string; color: string }> = {
  A: { bg: "rgba(155,227,177,0.10)", border: "rgba(155,227,177,0.25)", color: "#9be3b1" },
  B: { bg: "rgba(132,204,22,0.08)", border: "rgba(132,204,22,0.22)", color: "#84cc16" },
  C: { bg: "rgba(250,204,21,0.08)", border: "rgba(250,204,21,0.20)", color: "#facc15" },
  D: { bg: "rgba(251,146,60,0.08)", border: "rgba(251,146,60,0.20)", color: "#fb923c" },
  F: { bg: "rgba(255,138,138,0.08)", border: "rgba(255,138,138,0.20)", color: "#ff8a8a" },
};

function GradePill({ grade }: { grade: string }) {
  if (!grade) return null;
  const s = GRADE_BG[grade];
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold font-mono"
      style={{
        background: s?.bg ?? "transparent",
        border: `1px solid ${s?.border ?? "transparent"}`,
        color: s?.color ?? "inherit",
        letterSpacing: "0.06em",
      }}
      aria-label={`Grade ${grade}`}
    >
      {grade}
    </span>
  );
}

function StatusChip({ status }: { status: string }) {
  let cls = "yg-status-pending";
  let Icon = Clock;
  if (status === "succeeded") { cls = "yg-status-succeeded"; Icon = CheckCircle2; }
  else if (status === "failed") { cls = "yg-status-failed"; Icon = XCircle; }
  else if (status === "running") { cls = "yg-status-running"; Icon = ScanLine; }

  return (
    <span className={cls}>
      <Icon className="h-3 w-3" aria-hidden="true" />
      {status}
    </span>
  );
}

function ScanRow({ scan }: { scan: Scan }) {
  const started = new Date(scan.started_at).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  const finished = scan.finished_at
    ? new Date(scan.finished_at).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <div className="yg-scan-row">
      {/* Left: status chip + grade */}
      <div className="flex flex-col gap-2 min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <StatusChip status={scan.status} />
          {scan.grade && <GradePill grade={scan.grade} />}
          {scan.score != null && (
            <span
              className="text-xs font-semibold"
              style={{ color: "var(--yg-silver-1)" }}
            >
              Score: {scan.score}
            </span>
          )}
        </div>
        <div
          className="text-xs"
          style={{ color: "var(--yg-silver-3)", fontFamily: "var(--yg-font-mono)", letterSpacing: "0.04em" }}
        >
          Started {started}
          {finished && <span> · Finished {finished}</span>}
        </div>
        {scan.failure_reason && (
          <p
            className="text-xs mt-1 rounded-md px-3 py-2"
            style={{
              color: "#ff8a8a",
              background: "rgba(255,138,138,0.06)",
              border: "1px solid rgba(255,138,138,0.18)",
            }}
            role="alert"
          >
            {scan.failure_reason}
          </p>
        )}
      </div>
    </div>
  );
}

function ScansEmpty() {
  return (
    <div className="yg-empty-state">
      <div className="yg-empty-icon" aria-hidden="true">
        <ScanLine className="h-6 w-6" />
      </div>
      <div className="yg-empty-title">No scans yet</div>
      <p className="yg-empty-body">
        Compliance scans run automatically after domain import. Check back shortly —
        each scan audits SPF, DKIM, DMARC, and BIMI records for this domain.
      </p>
    </div>
  );
}

export function ScanDetailPage() {
  const { domainId } = useParams<{ domainId: string }>();

  const q = useQuery<Scan[]>({
    queryKey: ["yahoogle", "scans", domainId],
    queryFn: () => fetchScans(domainId!),
    enabled: !!domainId,
  });

  return (
    <div className="space-y-6">
      {/* Back navigation */}
      <div>
        <Link
          to="/domains"
          className="inline-flex items-center gap-1.5 text-sm transition-colors"
          style={{ color: "var(--yg-silver-3)", textDecoration: "none" }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "var(--yg-silver-0)"; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "var(--yg-silver-3)"; }}
          aria-label="Back to domains"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Domains
        </Link>
      </div>

      <div>
        <span className="yg-eyebrow">Audit history</span>
        <h1 className="yg-page-heading text-2xl">Scan History</h1>
        <p className="yg-page-sub">
          Recent compliance scans for this domain — newest first.
        </p>
      </div>

      {q.isLoading && <LoadingState rows={4} />}
      {q.error && <ErrorState error={q.error} retry={() => q.refetch()} />}

      {q.data && q.data.length === 0 && <ScansEmpty />}

      {q.data && q.data.length > 0 && (
        <div className="flex flex-col gap-2">
          {q.data.map((s) => (
            <ScanRow key={s.id} scan={s} />
          ))}
        </div>
      )}

      <Footer />
    </div>
  );
}
